import React, { useState } from 'react';

import AlertBanner from '~/components/core/AlertBanner';
import CancelButton from '~/components/core/Buttons/CancelButton';
import Text from '~/components/core/TextComponents/Text';
import FileDropZone from '~/components/FileDropZone';
import { XlsFileIcon } from '~/components/icons';

import type { SheetUploadInfo, TemplateUploadResponse } from './hooks/useReportTemplates';
import { useReportTemplates } from './hooks/useReportTemplates';
import { REPORT_TEMPLATES_TEXTS } from './texts';
import type { FormValues, SheetsValidationState } from './types';

export interface UploadResult {
  uploadResponse: TemplateUploadResponse;
  initialFormValues: FormValues;
  sheetsValidationState: SheetsValidationState;
}

interface UploadReportFileStepProps {
  onUploadComplete: (data: UploadResult) => void;
  onCancel: () => void;
}

const UploadReportFileStep: React.FC<UploadReportFileStepProps> = ({ onUploadComplete, onCancel }) => {
  const { uploadTemplate, isSubmitting, uploadProgress } = useReportTemplates();
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | undefined>(undefined);

  const handleFileSelect = async (selectedFile: File) => {
    setError(null);
    setFile(selectedFile);

    // Upload and parse the file
    const result = await uploadTemplate(selectedFile);
    if (!result.success) {
      // Clear the file so the user can immediately retry with a valid one
      setFile(undefined);
      setError(result.userErrorMessage);
      return;
    }

    // Initialize validation state for each sheet
    const validationState: UploadResult['sheetsValidationState'] = {};
    const sheets = result.data.sheets ?? [];
    sheets.forEach((sheet: SheetUploadInfo) => {
      validationState[sheet.sheet_name] = {
        matchedVariables: [],
        unmatchedVariables: [],
        isValidating: false,
        extracted_variables: sheet.extracted_variables ?? [],
      };
    });

    // Set initial form values
    const initialFormValues: FormValues = {
      templateName: selectedFile.name.replace(/\.(xlsx|xlsm)$/i, ''),
      sheets: sheets.map((sheet: SheetUploadInfo) => ({
        sheet_name: sheet.sheet_name,
        display_name: sheet.sheet_name,
        metabase_question_id: null,
      })),
    };

    onUploadComplete({
      uploadResponse: result.data,
      initialFormValues,
      sheetsValidationState: validationState,
    });
  };

  const handleCancelFileSelect = () => {
    setFile(undefined);
    setError(null);
  };

  return (
    <div>
      {error && <AlertBanner note={error} alertType={AlertBanner.ALERT_TYPES.ERROR} className="mb-16" />}

      <FileDropZone
        uploadPercent={uploadProgress}
        file={file}
        onFileSelect={handleFileSelect}
        onCancelFileSelect={handleCancelFileSelect}
        dropZoneOptions={{
          accept: ['.xlsx', '.xlsm'],
          onDropRejected: (fileRejections) => {
            const rejection = fileRejections[0];
            if (rejection?.errors[0]?.code === 'file-invalid-type') {
              setError(REPORT_TEMPLATES_TEXTS.FILE_TYPE_ERROR);
            } else {
              setError(rejection?.errors[0]?.message || REPORT_TEMPLATES_TEXTS.FILE_TYPE_ERROR);
            }
          },
        }}
        baseStyleOverride={{
          width: '100%',
          height: '200px',
        }}
        iconComponent={<XlsFileIcon className="h-[45px] w-[36px]" />}
        disabled={isSubmitting}
      />

      <Text variant={Text.VARIANTS.XS} className="mt-12 text-center">
        {REPORT_TEMPLATES_TEXTS.UPLOAD_INSTRUCTION}
      </Text>

      <div className="mt-16 flex w-full justify-end">
        <CancelButton disabled={isSubmitting} onClick={onCancel} />
      </div>
    </div>
  );
};

export default UploadReportFileStep;
